import type React from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createContainer,
	createHook,
	createStore,
	type Action,
	type OverrideContainerComponent,
} from '@atlassian/react-sweet-state';
import { createLocalStorageProvider } from '@jira/platform__browser-storage-providers/src/controllers/local-storage/index.tsx';

interface PopoutState {
	lastVisitedSearch: string | null;
	content: React.ReactNode;
}

const LOCAL_STORAGE_KEY_LAST_VISITED_SEARCH = 'jira-ops-alert-filters-last-visited-search';
const localStorageProvider = createLocalStorageProvider('alert_filters_popout');

const setAlertFiltersLastVisitedInLocalStorage = (value: PopoutState['lastVisitedSearch']) => {
	try {
		// @browser-storage setter
		localStorageProvider.set(LOCAL_STORAGE_KEY_LAST_VISITED_SEARCH, value);
	} catch (_) {
		return false;
	}
	return true;
};
const getAlertFiltersLastVisitedFromLocalStorage = (): string | undefined => {
	let item;
	try {
		item = localStorageProvider.get(LOCAL_STORAGE_KEY_LAST_VISITED_SEARCH);
	} catch (_) {
		return undefined;
	}
	return item;
};

const getOnInit = () => {
	return {
		lastVisitedSearch: getAlertFiltersLastVisitedFromLocalStorage(),
	};
};

const actions = {
	setLastVisitedSearch:
		(value: string | null): Action<PopoutState> =>
		({ getState, setState }) => {
			if (getState().lastVisitedSearch) {
				setState({ ...getState(), lastVisitedSearch: value });
				setAlertFiltersLastVisitedInLocalStorage(value);
			}
		},
	togglePoppedOut:
		(value: string): Action<PopoutState> =>
		({ getState, setState }) => {
			if (!getState().lastVisitedSearch) {
				setState({ ...getState(), lastVisitedSearch: value });
				setAlertFiltersLastVisitedInLocalStorage(value);
			} else {
				setState({ ...getState(), lastVisitedSearch: null });
				setAlertFiltersLastVisitedInLocalStorage(null);
			}
		},
	syncLastVisitedSearch:
		(): Action<PopoutState> =>
		({ getState, setState }) => {
			setState({ ...getState(), lastVisitedSearch: getAlertFiltersLastVisitedFromLocalStorage() });
		},
	setContent:
		(content: React.ReactNode): Action<PopoutState> =>
		({ getState, setState }) => {
			setState({ ...getState(), content });
		},
};

const Store = createStore<PopoutState, typeof actions>({
	initialState: {
		content: null,
		lastVisitedSearch: ' ',
	},
	actions,
	name: 'AlertsPopoutStore',
});
export const AlertsPopoutStoreContainer: OverrideContainerComponent<unknown> = createContainer(
	Store,
	{
		onInit:
			() =>
			({ setState }) => {
				setState(getOnInit());
			},
	},
);

const useHook = createHook(Store);
export const useAlertsPopoutStore = (): {
	setLastVisitedSearch: (value: string | null) => void | Promise<void>;
	togglePoppedOut: (value: string) => void | Promise<void>;
	syncLastVisitedSearch: () => void | Promise<void>;
	setContent: (content: React.ReactNode) => void | Promise<void>;
	state: PopoutState;
} => {
	const [state, hookActions] = useHook();
	return {
		state,
		...hookActions,
	};
};
